import React, { useState } from 'react';
import cn from 'classnames';
import Modal from '@components/Modals';
import { capitalize } from '@support/string';
import Button from '@components/Button';
import { Toggle } from '@components/FormInputs';
import styles from './Interstitial.module.scss';
import { TrackingFn } from '@components/Integrations/utils';
import {
  ExternalAuthProvider,
  getExternalAuthURL,
  openUrlOutsideElectron,
} from '@support/electron-external';
import { useIsElectron } from '@hooks/use-is-electron';
import { useAuthContext } from '@hooks/use-auth-context';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@support/hooks/use-theme';

interface InterstitialModalProps {
  accountType: ExternalAuthProvider;
  open: boolean;
  closeModal: () => void;
  forceDarkMode: boolean;
  trackConnection: TrackingFn;
}

export default function InterstitialModal({
  accountType,
  open,
  closeModal,
  forceDarkMode,
  trackConnection,
}: InterstitialModalProps) {
  const [theme] = useTheme();
  const { getEntitlementValue } = useAuthContext();
  const isElectron = useIsElectron();
  const location = useLocation();
  const userCanAddEmail = getEntitlementValue('integrations.email');

  const [enableCal, setEnableCal] = useState(true);
  const [enableEmail, setEnableEmail] = useState(!!userCanAddEmail);
  const accountLabel = capitalize(accountType);

  const redirectToAuth = async () => {
    const scopes = [enableCal && 'calendar', userCanAddEmail && enableEmail && 'email']
      .filter((scope) => scope)
      .join(',');

    const type = accountType == 'google' ? 'Google' : 'Microsoft';
    // FIXME rewrite this
    // @ts-ignore
    trackConnection({ type, scopes: scopes.includes(',') ? 'all' : scopes });
    const { redirect } = await getExternalAuthURL(
      accountType,
      isElectron ? 'desktop' : 'web',
      scopes,
      location.pathname,
      'connect',
    );
    if (isElectron) {
      openUrlOutsideElectron(redirect);
    } else {
      window.location.href = redirect;
    }
    closeModal();
  };

  let emailIcon = '/img/contact/icons/source/email.svg';
  let calIcon = '/img/contact/icons/source/calendar.svg';

  if (!forceDarkMode && theme == 'light') {
    emailIcon = '/img/contact/icons/source/email_light.svg';
    calIcon = '/img/contact/icons/source/calendar_light.svg';
  }

  return (
    <Modal
      isOpen={open}
      className="modal-small"
      onRequestClose={closeModal}
      forceDarkMode={forceDarkMode}
    >
      <div className="modal-header">Allow access to your {accountLabel} Account</div>
      <div className={cn(styles.modal, 'modal-content-padded')}>
        <p>
          Please connect your preferred accounts.{' '}
          {userCanAddEmail && <>Most members connect both calendar and email to start.</>}
        </p>
        <p>
          Remember, Clay cannot edit or delete your data, and everything is stored{' '}
          <a href="https://clay.earth/security-and-privacy" target="_blank">
            privately &amp; securely
          </a>
          .
        </p>
        <div className={styles.toggles}>
          <Toggle checked={enableCal} onChange={() => setEnableCal(!enableCal)}>
            <span className={cn(styles.scopeLabel, enableCal && styles.scopeEnabled)}>
              <img src={calIcon} /> {accountLabel} Calendar
            </span>
          </Toggle>
          {userCanAddEmail && (
            <Toggle checked={enableEmail} onChange={() => setEnableEmail(!enableEmail)}>
              <span className={cn(styles.scopeLabel, enableEmail && styles.scopeEnabled)}>
                <img src={emailIcon} /> {accountLabel} Email
              </span>
            </Toggle>
          )}
        </div>
        <div className={styles.btnRow}>
          <Button className="btn btn-muted" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            disabled={!enableCal && !enableEmail}
            onClick={redirectToAuth}
          >
            Connect
          </Button>
        </div>
      </div>
    </Modal>
  );
}
